import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendEmailVerification
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { apiRequest } from '../services/api';

// Create the authentication context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [providerData, setProviderData] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null); // null, 'not_submitted', 'submitted', 'approved'

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Get the ID token and store it in localStorage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);

      return userCredential.user;
    } catch (error) {
      setError('Failed to sign in: ' + error.message);
      throw error;
    }
  };

  // Sign up with email and password
  const signUp = async (email, password) => {
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Get the ID token and store it in localStorage
      const token = await userCredential.user.getIdToken();
      localStorage.setItem('authToken', token);

      return userCredential.user;
    } catch (error) {
      setError('Failed to sign up: ' + error.message);
      throw error;
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Get the ID token and store it in localStorage
      const token = await result.user.getIdToken();
      localStorage.setItem('authToken', token);

      return result.user;
    } catch (error) {
      setError('Failed to sign in with Google: ' + error.message);
      throw error;
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setError('');
      await firebaseSignOut(auth);

      // Clear the token from localStorage
      localStorage.removeItem('authToken');
      setProviderData(null);
      setProviderStatus(null);
    } catch (error) {
      setError('Failed to sign out: ' + error.message);
      throw error;
    }
  };

  // Re-fetch the current provider from the backend and update shared state
  // WITHOUT a full page reload (so the user stays on their current tab).
  // Returns the refreshed, merged provider object (or null).
  const refreshProviderData = async () => {
    try {
      const userData = await apiRequest('/users/me');
      if (userData?.provider) {
        const providerDataWithUser = {
          ...userData.provider,
          user: {
            id: userData.id,
            name: userData.name,
            email: userData.email,
            emailVerified: userData.emailVerified,
            country: userData.country,
            countryDto: userData.countryDto,
            city: userData.city,
            address: userData.address,
            phone: userData.phone,
            lat: userData.lat,
            lng: userData.lng,
          },
        };
        setProviderData(providerDataWithUser);
        return providerDataWithUser;
      }
      setProviderData(null);
      return null;
    } catch (apiError) {
      console.error('Failed to refresh provider data:', apiError);
      return null;
    }
  };

  // Check provider status
  const checkProviderStatus = async (user) => {
    if (!user) {
      setProviderStatus(null);
      setProviderData(null);
      return;
    }

    try {
      // Get the ID token with force refresh to ensure we have the latest claims
      const token = await user.getIdToken(true);

      // Store the token in localStorage for API requests
      localStorage.setItem('authToken', token);

      // Make a request to the backend to check provider status using centralized API
      try {
        const userData = await apiRequest('/users/me');
        
        if (userData.provider) {
          // Merge user data with provider data so components can access user.country
          const providerDataWithUser = {
            ...userData.provider,
            user: {
              id: userData.id,
              name: userData.name,
              email: userData.email,
              emailVerified: userData.emailVerified,
              country: userData.country,
              countryDto: userData.countryDto,
              city: userData.city,
              address: userData.address,
              phone: userData.phone,
              lat: userData.lat,
              lng: userData.lng
            }
          };
          setProviderData(providerDataWithUser);
          
          // Set provider status based on providerState
          if (userData.provider.providerState === 'APPROVED') {
            setProviderStatus('approved');
          } else if (userData.provider.providerState === 'SUBMITTED' || userData.provider.providerState === 'DECLINED') {
            setProviderStatus('submitted');
          } else {
            setProviderStatus('not_submitted');
          }
        } else {
          // No provider application
          setProviderData(null);
          setProviderStatus('not_submitted');
        }
      } catch (apiError) {
        // The Firebase token is valid, but its email already belongs to a different account,
        // so the backend has no usable user row for it (409 from the token filter). Surface a
        // clear "sign in with your original account" state instead of the generic dashboard.
        if (apiError?.status === 409 && apiError?.body?.code === 'EMAIL_ALREADY_REGISTERED') {
          setProviderData(null);
          setProviderStatus('email_conflict');
          return;
        }
        console.error('API request failed:', apiError);
        setProviderStatus('not_submitted');
      }
    } catch (error) {
      console.error('Error checking provider status:', error);
      setProviderStatus('not_submitted');
    }
  };

  // Subscribe to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Get a fresh token and store it in localStorage
        try {
          const token = await user.getIdToken(true);
          localStorage.setItem('authToken', token);
        } catch (error) {
          console.error('Error getting token:', error);
        }

        await checkProviderStatus(user);
      } else {
        setProviderStatus(null);
        setProviderData(null);
        localStorage.removeItem('authToken');
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Password signup verifies nothing. The verified-email claim path requires a verified
  // address, so without this the fallback only works for Google users -- and Google
  // sign-in already arrives verified, so it would cover none of the cases it exists for.
  const sendVerificationEmail = async () => {
    if (!auth.currentUser) return false;
    try {
      await sendEmailVerification(auth.currentUser);
      return true;
    } catch (err) {
      console.error('Failed to send verification email', err);
      return false;
    }
  };

  // Firebase caches emailVerified on the client. After the user clicks the link in
  // their inbox, nothing here changes until the record is reloaded.
  const reloadUser = async () => {
    if (!auth.currentUser) return false;
    await auth.currentUser.reload();
    await auth.currentUser.getIdToken(true);
    return auth.currentUser.emailVerified;
  };

  const value = {
    currentUser,
    providerData,
    providerStatus,
    loading,
    error,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    checkProviderStatus,
    refreshProviderData,
    sendVerificationEmail,
    reloadUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
